// components/scrollablePanel.js
"use client";

import React, { useRef, useState, useEffect, useCallback } from "react";
import clsx from "clsx";
import { isFilled } from "@prismicio/helpers";
import { RichText } from "@/app/lib/RichText";
import { ConditionalLink } from "@/app/lib/ConditionalLink";
import { RevealOnScroll } from "@/app/lib/RevealOnScroll";
import { PrismicNextImage } from "@prismicio/next";
import { Settings } from "@/app/lib/settings";
import { isHoverCapable } from "@/app/lib/utils";
import styles from "./scrollablePanel.module.scss";

export default function ScrollablePanel({ items, type }) {
	const scrollContainerRef = useRef(null);
	const animationFrameRef = useRef(null);
	const [scrollDirection, setScrollDirection] = useState("");

	const SCROLL_SPEED_BASE = 3;

	const startScrolling = useCallback((direction, speed) => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const scroll = () => {
			if (direction === "left") {
				container.scrollLeft -= speed;
				if (container.scrollLeft <= 0) stopScrolling();
			} else {
				container.scrollLeft += speed;
				if (container.scrollLeft >= container.scrollWidth - container.clientWidth) {
					stopScrolling();
				}
			}
			animationFrameRef.current = requestAnimationFrame(scroll);
		};

		stopScrolling();
		animationFrameRef.current = requestAnimationFrame(scroll);
	}, []);

	const stopScrolling = useCallback(() => {
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}
	}, []);

	const handleMouseMove = useCallback(
		(e) => {
			const container = scrollContainerRef.current;
			if (!container) return;

			const { left, right, width } = container.getBoundingClientRect();
			const mouseX = e.clientX;

			// Adjust the thresholds to 25% from the left and right edges
			const leftThreshold = left + width * 0.20;
			const rightThreshold = right - width * 0.20;

			let currentScrollSpeed = 0;

			if (mouseX < leftThreshold) {
				const distanceToEdge = leftThreshold - mouseX;
				currentScrollSpeed =
					SCROLL_SPEED_BASE + (distanceToEdge / width) * SCROLL_SPEED_BASE * 5;

				setScrollDirection("left");
				startScrolling("left", currentScrollSpeed);
			} else if (mouseX > rightThreshold) {
				const distanceToEdge = mouseX - rightThreshold;
				currentScrollSpeed =
					SCROLL_SPEED_BASE + (distanceToEdge / width) * SCROLL_SPEED_BASE * 5;

				setScrollDirection("right");
				startScrolling("right", currentScrollSpeed);
			} else {
				stopScrolling();
				setScrollDirection("");
			}
		},
		[startScrolling, stopScrolling]
	);

	const handleMouseLeave = useCallback(() => {
		stopScrolling();
		setScrollDirection("");
	}, [stopScrolling]);

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		if (isHoverCapable()) {
			// Desktop hover-capable
			container.addEventListener("mousemove", handleMouseMove);
			container.addEventListener("mouseleave", handleMouseLeave);

			return () => {
				container.removeEventListener("mousemove", handleMouseMove);
				container.removeEventListener("mouseleave", handleMouseLeave);
				stopScrolling();
			};
		} else {
			// Mobile / touch → enable smooth finger scrolling
			container.style.overflowX = "auto";
			container.style.scrollSnapType = "x mandatory"; // nice UX
			container.style.webkitOverflowScrolling = "touch"; // iOS momentum
		}

		return () => stopScrolling();
	}, [handleMouseMove, handleMouseLeave, stopScrolling]);

	const getWrapperClasses = () =>
		clsx(
			styles.main,
			"cursor-scroll",
			scrollDirection === "left"
				? "cursor-scroll-left"
				: scrollDirection === "right"
					? "cursor-scroll-right"
					: ""
		);

	if (type === "stories" && items?.length) {
		return (
			<div className={clsx(getWrapperClasses(), styles.stories)} ref={scrollContainerRef}>
				{items.map((story, index) => (
					<RevealOnScroll scrollDelayRem={index * 1} key={index}>
						<div className={clsx(styles.box)}>
							<ConditionalLink
								condition={story?.data?.slices?.length || isFilled.link(story?.data?.external_url)}
								url={story?.data?.slices?.length ? `/${Settings.storiesPrefix}/${story?.uid}` : story?.data?.external_url}
								className={clsx(styles.inner)}
							>
								{isFilled.image(story?.data?.image?.Portrait) && (
									<div className={styles.image_wrapper}>
										<PrismicNextImage
											field={story?.data?.image?.Portrait}
											fallbackAlt=""
											className={clsx(styles.image)}
										/>
									</div>
								)}

								{isFilled.select(story?.data?.type) && (
									<p className={clsx(styles.type)}>{story?.data?.type}</p>
								)}

								{isFilled.keyText(story?.data?.title) && (
									<h3 className={clsx(styles.item_title)}>{story?.data?.title}</h3>
								)}

								{isFilled.richText(story?.data?.excerpt) && (
									<div className={styles.introduction}>
										<RichText field={story?.data?.excerpt} />
									</div>
								)}

							</ConditionalLink>
						</div>
					</RevealOnScroll>
				))}
			</div>
		);

	} else if (type === 'grid' && items?.length) {
		return (
			<div className={clsx(getWrapperClasses(), styles.grid)} ref={scrollContainerRef}>
				{items.map((item, index) => (
					<RevealOnScroll scrollDelayRem={index * 1} key={index}>
						<div className={clsx(styles.box)}>
							<ConditionalLink
								condition={(item?.link?.uid || item?.link?.url) !== undefined}
								url={item?.link}
								className={clsx(styles.inner)}
							>
								{isFilled.image(item?.image) && (
									<div className={styles.image_wrapper}>
										<PrismicNextImage field={item?.image} fallbackAlt='' className={clsx(styles.image)} />
									</div>
								)}

								{isFilled.keyText(item?.title) && (
									<h3 className={clsx(styles.type)}>{item?.title}</h3>
								)}

								{isFilled.richText(item?.content) && (
									<div className={styles.tagline}>
										<RichText field={item?.content} />
									</div>
								)}
							</ConditionalLink>
						</div>
					</RevealOnScroll>
				))}
			</div>
		);

	} else if (type === 'projects' && items?.length) {
		return (
			<div className={clsx(getWrapperClasses(), styles.projects)} ref={scrollContainerRef}>
				{items.map((project, index) => {
					return (
						<RevealOnScroll scrollDelayRem={index * 1} key={index}>
							<div className={clsx(styles.box)}>
								<ConditionalLink condition={project?.data?.slices?.length} url={`/${Settings.projectsPrefix}/${project?.uid}`} className={clsx(styles.inner)}>
									{isFilled.image(project?.data?.image) && (
										<div className={styles.image_wrapper}>
											<PrismicNextImage field={project?.data?.image} fallbackAlt='' className={clsx(styles.image)} />
										</div>
									)}

									{isFilled.keyText(project?.data?.title) && (
										<h3 className={clsx(styles.type)}>{project?.data?.title}</h3>
									)}

									{isFilled.keyText(project?.data?.tagline) && (
										<p className={clsx(styles.tagline)}>{project?.data?.tagline}</p>
									)}

									{isFilled.richText(project?.data?.introduction) && (
										<div className={styles.introduction}>
											<RichText field={project?.data?.introduction} />
										</div>
									)}
								</ConditionalLink>
							</div>
						</RevealOnScroll>
					)
				})}
			</div>
		)

	} else if ((type === 'columns' || type === 'dark_columns') && items?.length) {
		return (
			<div className={clsx(getWrapperClasses(), styles.columns, styles[type])} ref={scrollContainerRef}>
				{items.map((item, index) => {
					return (
						<RevealOnScroll scrollDelayRem={index * 1} key={index}>
							<div
								className={clsx(styles.panel)}
								key={index}
								style={{
									backgroundColor: item?.background_color ? item.background_color : '',
									color: item?.text_color ? item.text_color : '',
								}}
							>

								{isFilled.image(item?.image) && (
									<div className={styles.image_wrapper}>
										<PrismicNextImage field={item?.image} className={clsx(styles.image)} fallbackAlt='' />
									</div>
								)}

								{(isFilled.richText(item?.text)) && (
									<div className={clsx(styles.body)}>
										<RichText field={item.text} />
									</div>
								)}
							</div>
						</RevealOnScroll>
					)
				})}
			</div>
		)
	}

	return null;
}